<template>
  <div class="test-room">
    <!-- Timer bar (agar kerak bo'lsa) -->
    <div v-if="showTimer" class="timer-bar">
      <div class="timer-card" :class="{ 'timer-warning': timeLeft <= 300 }">
        <div class="timer-content">
          <span class="timer-label">
            <ClockCircleOutlined /> Qolgan vaqt:
          </span>
          <span class="timer-value">{{ formatTime(timeLeft) }}</span>
        </div>
      </div>
    </div>

    <a-row :gutter="[14, 14]">
      <a-col :xs="24" :xl="12">
        <a-card class="custom-card panel-card">
          <template #title>
            <div class="title-row">
              <div>
                <div class="test-title">{{ title }}</div>
                <div class="test-subtitle">
                  Savol {{ currentIndex + 1 }} / {{ questions.length }}
                  <span class="mistake-badge" :class="{ 'mistake-limit': wrongCount >= 2 }">
                    Xatolar: {{ wrongCount }} / 3
                  </span>
                </div>
              </div>
              <a-tag color="blue" class="hotkey-tag">F1-F{{ currentQuestion?.options?.length || 6 }}</a-tag>
            </div>
          </template>

          <h2 class="question-title">{{ currentQuestion?.text || 'Savol topilmadi' }}</h2>

          <div class="options">
            <a-button
              v-for="(option, idx) in currentQuestion?.options || []"
              :key="option.id"
              block
              class="option-btn"
              :class="{
                'option-correct': showExplanation && option.is_correct,
                'option-wrong': showExplanation && 
                  selectedAnswerId === option.id && 
                  !option.is_correct
              }"
              :type="selectedAnswerId === option.id ? 'primary' : 'default'"
              @click="selectAnswer(option.id, idx)"
              :disabled="showExplanation || isFinished"
            >
              <span class="option-hotkey">F{{ idx + 1 }}</span>
              <span class="option-text">{{ option.text }}</span>
              <CheckCircleFilled 
                v-if="showExplanation && option.is_correct" 
                class="correct-icon" 
              />
              <CloseCircleFilled 
                v-else-if="showExplanation && selectedAnswerId === option.id && !option.is_correct" 
                class="wrong-icon" 
              />
            </a-button>
          </div>
        </a-card>
      </a-col>

      <a-col :xs="24" :xl="12">
        <a-card class="custom-card panel-card">
          <div class="image-wrap">
            <!-- Rasm mavjud bo'lsa ko'rsatish -->
            <template v-if="hasImage">
              <img 
                :src="getImageUrl(currentQuestion?.image_url)" 
                class="question-image" 
                alt="Savol rasmi" 
                @error="handleImageError"
                @load="handleImageLoad"
              />
            </template>
            <!-- Rasm yo'q bo'lsa placeholder -->
            <div v-else class="no-image-placeholder">
              <div class="placeholder-content">
                <img src="../../public//no-image.png" alt="No image" class="placeholder-icon" />
                <span class="placeholder-text">Rasm mavjud emas</span>
              </div>
            </div>
          </div>

          <div v-if="showExplanation" class="explanation">
            <div class="explanation-title">
              <BulbOutlined /> Izoh
            </div>
            <p class="explanation-text">{{ currentQuestion?.explanation || 'Izoh mavjud emas' }}</p>
            
            <a-alert
              :type="selectedAnswerId ? (isCurrentAnswerCorrect ? 'success' : 'error') : 'info'"
              show-icon
              class="result-alert"
            >
              <template #message>
                <div class="alert-message">
                  <span v-if="selectedAnswerId && isCurrentAnswerCorrect">
                    <CheckCircleOutlined /> To‘g‘ri javob! 
                    <span v-if="currentQuestion?.previous_wrong_answer" class="success-note">
                      (Xato savollar ro'yxatidan o'chirildi)
                    </span>
                  </span>
                  <span v-else-if="selectedAnswerId && !isCurrentAnswerCorrect">
                    <CloseCircleOutlined /> Noto‘g‘ri javob
                  </span>
                  <span v-else>
                    <InfoCircleOutlined /> Javob tanlanmadi
                  </span>
                </div>
              </template>
            </a-alert>

            <!-- Previous wrong answer info -->
            <div v-if="currentQuestion?.previous_wrong_answer" class="previous-wrong">
              <InfoCircleOutlined /> Oldingi xato javobingiz: 
              <strong>{{ currentQuestion.previous_wrong_answer.text }}</strong>
            </div>
          </div>
        </a-card>
      </a-col>
    </a-row>

    <!-- Question navigation -->
    <div class="fixed-bottom-bar">
      <div class="question-number-grid">
        <button
          v-for="(q, index) in questions"
          :key="q.id"
          class="question-btn"
          :class="getQuestionBtnClass(index)"
          @click="goToQuestion(index)"
          :disabled="!showExplanation || isFinished"
        >
          {{ index + 1 }}
          <span v-if="wrongMap[index]" class="btn-badge">!</span>
        </button>
      </div>
    </div>

    <!-- Finish confirm modal -->
    <a-modal
      v-model:visible="showFinishConfirm"
      title="Testni yakunlash"
      @ok="submitFinish"
      @cancel="showFinishConfirm = false"
      ok-text="Yakunlash"
      cancel-text="Bekor qilish"
      :confirm-loading="finishing"
      centered
    >
      <div class="finish-summary">
        <p>Testni yakunlamoqchimisiz?</p>
        <div class="stats-summary">
          <div class="stat-item">
            <span class="stat-label">✅ To'g'ri javoblar:</span>
            <span class="stat-value correct">{{ correctCount }}</span>
          </div>
          <div class="stat-item">
            <span class="stat-label">❌ Noto'g'ri javoblar:</span>
            <span class="stat-value wrong">{{ wrongCount }}</span>
          </div>
          <div class="stat-item">
            <span class="stat-label">⏭️ Javob berilmagan:</span>
            <span class="stat-value">{{ unansweredCountComputed }}</span>
          </div>
        </div>
      </div>
    </a-modal>
  </div>
</template>

<script>
import { Modal, message } from 'ant-design-vue'
import {
  BulbOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  CheckCircleFilled,
  CloseCircleFilled,
  InfoCircleOutlined,
  ClockCircleOutlined
} from '@ant-design/icons-vue'
import axios from 'axios'

export default {
  name: 'RealExamRoom',
  components: {
    BulbOutlined,
    CheckCircleOutlined,
    CloseCircleOutlined,
    CheckCircleFilled,
    CloseCircleFilled,
    InfoCircleOutlined,
    ClockCircleOutlined
  },
  props: {
    title: {
      type: String,
      default: 'Real imtihon'
    },
    questions: {
      type: Array,
      default: () => []
    },
    resultId: {
      type: Number,
      required: true
    },
    backRoute: {
      type: String,
      default: '/real-exam'
    },
    showTimer: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      currentIndex: 0,
      selectedAnswerId: null,
      showExplanation: false,
      answeredMap: {},
      wrongMap: {},
      correctCount: 0,
      wrongCount: 0,
      isFinished: false,
      finishing: false,
      showFinishConfirm: false,
      startTime: Date.now(),
      timeLeft: 3600,
      timerInterval: null,
      answers: [],
      autoNextTimeout: null,
      imageLoadError: false
    }
  },

  computed: {
    currentQuestion() {
      return this.questions[this.currentIndex] || {}
    },

    totalQuestions() {
      return this.questions.length
    },

    unansweredCountComputed() {
      return this.totalQuestions - this.correctCount - this.wrongCount
    },

    isCurrentAnswerCorrect() {
      if (!this.selectedAnswerId || !this.currentQuestion?.options) return false
      const selectedOption = this.currentQuestion.options.find(opt => opt.id === this.selectedAnswerId)
      return selectedOption ? selectedOption.is_correct : false
    },

    hasImage() {
      const imageUrl = this.currentQuestion?.image_url
      return imageUrl && imageUrl.trim() !== '' && !this.imageLoadError
    }
  },

  mounted() {
    if (this.showTimer) {
      this.startTimer()
    }
    window.addEventListener('keydown', this.handleKeydown)
    
    this.questions.forEach((_, index) => {
      this.answeredMap[index] = false
      this.wrongMap[index] = false
    })
  },

  beforeUnmount() {
    window.removeEventListener('keydown', this.handleKeydown)
    if (this.timerInterval) {
      clearInterval(this.timerInterval)
    }
    if (this.autoNextTimeout) {
      clearTimeout(this.autoNextTimeout)
    }
  },

  methods: {
    startTimer() {
      this.timerInterval = setInterval(() => {
        if (!this.isFinished) {
          this.timeLeft -= 1
          if (this.timeLeft <= 0) {
            this.autoFinishTest()
          }
        }
      }, 1000)
    },

    formatTime(seconds) {
      const mins = Math.floor(Math.max(0, seconds) / 60)
      const secs = Math.floor(Math.max(0, seconds) % 60)
      return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
    },

    handleKeydown(e) {
      if (this.showExplanation || this.isFinished) return

      const key = e.key
      if (key.startsWith('F') && !isNaN(key.slice(1))) {
        e.preventDefault()
        const index = parseInt(key.slice(1)) - 1
        const option = this.currentQuestion?.options?.[index]
        if (option) {
          this.selectAnswer(option.id, index)
        }
      }
    },

    selectAnswer(optionId, optionIndex) {
      if (this.showExplanation || this.isFinished) return

      Modal.confirm({
        title: 'Javobni tasdiqlash',
        content: `Siz F${optionIndex + 1} variantini tanladingiz. Tasdiqlaysizmi?`,
        okText: 'Tasdiqlash',
        cancelText: 'Bekor qilish',
        centered: true,
        onOk: () => {
          this.processAnswer(optionId)
        }
      })
    },

    processAnswer(optionId) {
      this.selectedAnswerId = optionId
      this.showExplanation = true
      this.answeredMap[this.currentIndex] = true

      const selectedOption = this.currentQuestion.options.find(opt => opt.id === optionId)
      const isCorrect = selectedOption ? selectedOption.is_correct : false

      this.answers.push({
        question_id: this.currentQuestion.id,
        selected_option_id: optionId,
        is_correct: isCorrect
      })

      if (isCorrect) {
        this.correctCount++
        message.success('To‘g‘ri javob!')
      } else {
        this.wrongCount++
        this.wrongMap[this.currentIndex] = true
        message.error('Noto‘g‘ri javob!')

        if (this.wrongCount >= 3) {
          setTimeout(() => {
            if (!this.isFinished) {
              this.finishTest()
            }
          }, 1500)
          return
        }
      }

      if (!isCorrect && this.currentQuestion.wrong_question_id) {
        this.resolveWrongQuestion(this.currentQuestion.wrong_question_id, optionId)
      }

      this.autoNextTimeout = setTimeout(() => {
        if (this.currentIndex < this.questions.length - 1) {
          this.nextQuestion()
        } else if (this.currentIndex === this.questions.length - 1) {
          this.finishTest()
        }
      }, 1500)
    },

    async resolveWrongQuestion(wrongQuestionId, selectedOptionId) {
      try {
        const token = localStorage.getItem('access_token')
        
        await axios.post(
          `/api/v1/tests/wrong-question-resolve/${wrongQuestionId}/`,
          { selected_option_id: selectedOptionId },
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        )
      } catch (err) {
        console.error('Error resolving wrong question:', err)
      }
    },

    nextQuestion() {
      if (this.autoNextTimeout) {
        clearTimeout(this.autoNextTimeout)
        this.autoNextTimeout = null
      }

      if (this.currentIndex < this.questions.length - 1) {
        this.currentIndex++
        this.selectedAnswerId = null
        this.showExplanation = false
        this.imageLoadError = false
      }
    },

    goToQuestion(index) {
      if (!this.showExplanation || this.isFinished) return
      
      if (this.autoNextTimeout) {
        clearTimeout(this.autoNextTimeout)
        this.autoNextTimeout = null
      }

      this.currentIndex = index
      this.selectedAnswerId = null
      this.showExplanation = false
      this.imageLoadError = false
    },

    finishTest() {
      if (this.autoNextTimeout) {
        clearTimeout(this.autoNextTimeout)
        this.autoNextTimeout = null
      }

      if (this.answers.length === this.questions.length) {
        this.submitFinish()
      } else {
        this.showFinishConfirm = true
      }
    },

    async submitFinish() {
      this.finishing = true
      this.showFinishConfirm = false

      try {
        const durationSeconds = Math.floor((Date.now() - this.startTime) / 1000)
        const token = localStorage.getItem('access_token')

        const response = await axios.post(
          '/api/v1/tests/finish/',
          {
            result_id: this.resultId,
            duration_seconds: durationSeconds,
            answers: this.answers
          },
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        )

        if (response.data) {
          this.isFinished = true
          if (this.timerInterval) {
            clearInterval(this.timerInterval)
          }

          const result = {
            title: this.title,
            totalQuestions: this.totalQuestions,
            correctCount: this.correctCount,
            wrongCount: this.wrongCount,
            percent: response.data.result.score_percent,
            failedByMistakes: this.wrongCount >= 3,
            backRoute: this.backRoute,
            resultData: response.data.result
          }

          sessionStorage.setItem('examResult', JSON.stringify(result))
          message.success('Test muvaffaqiyatli yakunlandi!')
          this.$router.push('/result')
        }
      } catch (err) {
        console.error('Finish test error:', err)
        message.error(err.response?.data?.detail || 'Testni yakunlashda xatolik yuz berdi')
      } finally {
        this.finishing = false
      }
    },

    autoFinishTest() {
      if (!this.isFinished) {
        message.warning('Vaqt tugadi! Test avtomatik yakunlanmoqda...')
        this.finishTest()
      }
    },

    getImageUrl(imageUrl) {
      if (!imageUrl) return ''
      if (imageUrl.startsWith('http')) return imageUrl
      if (imageUrl.startsWith('/media/')) {
        const baseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000'
        return `${baseUrl}${imageUrl}`
      }
      return imageUrl
    },

    handleImageError(e) {
      this.imageLoadError = true
      e.target.style.display = 'none'
    },

    handleImageLoad() {
      this.imageLoadError = false
    },

    getQuestionBtnClass(index) {
      return {
        'active': this.currentIndex === index,
        'answered': this.answeredMap[index] && !this.wrongMap[index],
        'wrong': this.wrongMap[index],
        'current': this.currentIndex === index
      }
    }
  }
}
</script>

<style scoped>
.test-room { 
  padding-bottom: 90px; 
  position: relative;
  background: var(--bg);
  min-height: 100vh;
}

.timer-bar {
  margin-bottom: 16px;
  padding: 0 24px;
}

.timer-card {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border: none;
  border-radius: 12px;
  max-width: 300px;
  margin: 0 auto;
}

.timer-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  color: white;
  padding: 10px 20px;
}

.timer-label {
  font-size: 14px;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 8px;
}

.timer-value {
  font-size: 22px;
  font-weight: 700;
  font-family: monospace;
  background: rgba(255, 255, 255, 0.2);
  padding: 4px 12px;
  border-radius: 8px;
}

.timer-warning {
  background: linear-gradient(135deg, #ff6b6b, #ee5a5a);
  animation: pulse 1s infinite;
}

@keyframes pulse {
  0% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.9; transform: scale(1.02); }
  100% { opacity: 1; transform: scale(1); }
}

.panel-card { 
  min-height: 500px;
  height: 100%;
}

.title-row { 
  display: flex; 
  justify-content: space-between; 
  align-items: flex-start; 
  gap: 10px; 
  flex-wrap: wrap;
}

.test-title { 
  font-size: 17px; 
  font-weight: 800; 
  color: var(--text); 
}

.test-subtitle { 
  font-size: 12px; 
  color: var(--text-soft); 
  margin-top: 3px; 
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
}

.mistake-badge { 
  display: inline-block; 
  padding: 2px 8px; 
  border-radius: 20px; 
  font-size: 11px; 
  font-weight: 700; 
  color: #fecaca; 
  background: rgba(239, 68, 68, 0.15); 
  border: 1px solid rgba(239, 68, 68, 0.3); 
}

.mistake-limit {
  background: rgba(239, 68, 68, 0.35);
  border-color: rgba(239, 68, 68, 0.8);
  animation: shake 0.5s ease;
}

@keyframes shake {
  0%, 100% { transform: translateX(0); }
  25% { transform: translateX(-3px); }
  75% { transform: translateX(3px); }
}

.hotkey-tag {
  flex-shrink: 0;
}

.question-title { 
  font-size: 20px; 
  line-height: 1.4; 
  margin: 0 0 20px; 
  color: var(--text); 
  font-weight: 700; 
  word-break: break-word;
}

.options { 
  display: flex; 
  flex-direction: column; 
  gap: 12px; 
}

.option-btn { 
  min-height: 52px; 
  height: auto; 
  border-radius: 14px !important; 
  display: flex !important; 
  justify-content: flex-start !important; 
  align-items: center; 
  padding: 12px 16px !important; 
  text-align: left !important; 
  position: relative;
  transition: all 0.2s ease;
}

.option-btn:hover:not(:disabled) {
  transform: translateX(4px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.option-correct {
  background: rgba(82, 196, 26, 0.15) !important;
  border-color: #52c41a !important;
  border-width: 2px !important;
}

.option-wrong {
  background: rgba(245, 34, 45, 0.15) !important;
  border-color: #f5222d !important;
  border-width: 2px !important;
}

.option-hotkey { 
  min-width: 40px; 
  height: 32px; 
  display: inline-flex; 
  align-items: center; 
  justify-content: center; 
  border-radius: 10px; 
  margin-right: 12px; 
  font-size: 12px; 
  font-weight: 800; 
  background: rgba(59, 130, 246, 0.2); 
  border: 1px solid var(--border); 
}

.option-text { 
  flex: 1; 
  white-space: normal; 
  line-height: 1.4; 
  font-size: 14px; 
}

.correct-icon, .wrong-icon {
  margin-left: 10px;
  font-size: 18px;
}

.correct-icon {
  color: #52c41a;
}

.wrong-icon {
  color: #f5222d;
}

.image-wrap { 
  width: 100%; 
  min-height: 260px; 
  border-radius: 16px; 
  overflow: hidden; 
  border: 1px solid var(--border); 
  background: rgba(0, 0, 0, 0.02); 
  display: flex; 
  align-items: center; 
  justify-content: center; 
}

.question-image { 
  width: 100%; 
  max-height: 320px; 
  object-fit: contain; 
  display: block; 
}

.no-image-placeholder {
  width: 100%;
  height: 100%;
  min-height: 260px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, rgba(100, 100, 100, 0.05), rgba(150, 150, 150, 0.1));
}

.placeholder-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 40px;
}

.placeholder-icon {
  width: 280px;
  height: 280px;
  opacity: 0.5;
}

.placeholder-text {
  font-size: 14px;
  color: var(--text-soft);
}

.explanation { 
  margin-top: 20px; 
  padding: 16px; 
  border-radius: 16px; 
  border: 1px solid var(--border); 
  background: rgba(0, 0, 0, 0.02); 
}

.explanation-title { 
  font-size: 15px; 
  font-weight: 700; 
  margin-bottom: 10px; 
  color: var(--text); 
  display: flex;
  align-items: center;
  gap: 8px;
}

.explanation-text { 
  color: var(--text-soft); 
  line-height: 1.6; 
  margin: 0 0 12px; 
  font-size: 14px; 
}

.result-alert {
  margin-top: 12px;
}

.alert-message {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
}

.success-note {
  font-size: 11px;
  opacity: 0.8;
  margin-left: 4px;
}

.previous-wrong {
  margin-top: 12px;
  padding: 10px 12px;
  background: rgba(250, 173, 20, 0.1);
  border-radius: 10px;
  font-size: 12px;
  color: #faad14;
  display: flex;
  align-items: center;
  gap: 8px;
  border-left: 3px solid #faad14;
}

.fixed-bottom-bar {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background: var(--card-bg);
  border-top: 1px solid var(--border);
  padding: 12px 16px;
  z-index: 100;
  backdrop-filter: blur(10px);
}

.question-number-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  justify-content: center;
  max-width: 100%;
  margin: 0 auto;
  padding: 0 16px;
}

.question-btn {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  border: 2px solid var(--border);
  background: var(--card-bg);
  color: var(--text);
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
}

.question-btn:hover:not(:disabled) {
  background: var(--primary);
  color: white;
  border-color: var(--primary);
  transform: translateY(-2px);
}

.question-btn.active {
  background: var(--primary);
  color: white;
  border-color: var(--primary);
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.4);
}

.question-btn.answered {
  background: rgba(82, 196, 26, 0.2);
  border-color: #52c41a;
  color: #52c41a;
}

.question-btn.wrong {
  background: rgba(245, 34, 45, 0.2);
  border-color: #f5222d;
  color: #f5222d;
}

.question-btn.current {
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.3);
}

.question-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  transform: none;
}

.btn-badge {
  position: absolute;
  top: -6px;
  right: -6px;
  width: 20px;
  height: 20px;
  background: #f5222d;
  color: white;
  border-radius: 50%;
  font-size: 11px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px solid var(--card-bg);
  font-weight: bold;
}

.finish-summary {
  padding: 8px 0;
}

.stats-summary {
  margin-top: 16px;
  padding: 12px;
  background: rgba(0, 0, 0, 0.05);
  border-radius: 12px;
}

.stat-item {
  display: flex;
  justify-content: space-between;
  padding: 8px 0;
  border-bottom: 1px solid var(--border);
}

.stat-item:last-child {
  border-bottom: none;
}

.stat-label {
  font-weight: 500;
  color: var(--text-soft);
}

.stat-value {
  font-weight: 700;
  font-size: 16px;
}

.stat-value.correct {
  color: #52c41a;
}

.stat-value.wrong {
  color: #f5222d;
}

/* Responsive */
@media (max-width: 768px) {
  .test-room {
    padding-bottom: 80px;
  }

  .timer-bar {
    padding: 0 16px;
  }

  .timer-card {
    max-width: 260px;
  }

  .timer-value {
    font-size: 18px;
    padding: 2px 10px;
  }

  .panel-card {
    min-height: auto;
  }

  .question-title {
    font-size: 18px;
  }

  .option-btn {
    min-height: 48px;
    padding: 10px 14px !important;
  }

  .option-hotkey {
    min-width: 36px;
    height: 28px;
    font-size: 11px;
  }

  .option-text {
    font-size: 13px;
  }

  .question-number-grid {
    gap: 6px;
  }

  .question-btn {
    width: 38px;
    height: 38px;
    font-size: 13px;
  }
}

@media (max-width: 576px) {
  .test-room {
    padding-bottom: 70px;
  }

  .title-row {
    flex-direction: column;
  }

  .test-title {
    font-size: 15px;
  }

  .test-subtitle {
    font-size: 11px;
  }

  .question-title {
    font-size: 16px;
    margin-bottom: 16px;
  }

  .option-btn {
    min-height: 44px;
    padding: 8px 12px !important;
  }

  .option-hotkey {
    min-width: 32px;
    height: 26px;
    font-size: 10px;
  }

  .option-text {
    font-size: 12px;
  }

  .question-btn {
    width: 34px;
    height: 34px;
    font-size: 12px;
  }

  .image-wrap {
    min-height: 200px;
  }

  .placeholder-icon {
    width: 60px;
    height: 60px;
  }
}
</style>